import { Typography } from '@mui/material';
import { formatDate } from '@strength/utils';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocale } from '../../../../utils/i18n';
import { PhaseSpan } from '../types';
import { BowlingDay } from './bowling-day';

/**
 * Shared wrapper for chart Tooltip content. Renders the framed white pill
 * with the formatted date and — when `day` falls inside one of the supplied
 * phases — that phase's name as a bold header above the date.
 */
export const TooltipShell = ({
  day,
  phases,
  children,
}: {
  day: string;
  phases: PhaseSpan[];
  children: ReactNode;
}) => {
  const formattedDate = formatDate(day, getLocale(), {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
  const phase = phases.find((p) => day >= p.startDate && day <= p.endDate);
  return (
    <div className="w-max rounded-md border border-gray-200 bg-white py-2 shadow-sm">
      <div className="mb-1.5 border-b border-gray-200 pb-1 px-3">
        {phase && (
          <Typography variant="body2" className="font-semibold text-gray-700">
            {phase.name}
          </Typography>
        )}
        <Typography variant="body2" className="text-gray-500">
          {formattedDate}
        </Typography>
      </div>
      {children}
    </div>
  );
};

/**
 * Single metric row inside a chart Tooltip — colored swatch + label on the
 * left, value on the right. Pass `last` for the bottom row to omit the
 * separator border.
 */
export const TooltipRow = ({
  color,
  label,
  subLabel,
  value,
  last = false,
}: {
  color: string;
  label: string;
  /** Optional secondary line under the label (e.g. the % Difference formula). */
  subLabel?: string;
  value: string;
  last?: boolean;
}) => (
  <div
    className={`text-sm text-gray-600 px-3 flex flex-row items-center justify-between gap-4 pt-1 ${
      last ? '' : 'border-b border-gray-200 pb-1'
    }`}
  >
    <div className="flex flex-row items-center gap-1.5">
      <div
        className="size-3 shrink-0 rounded-[2px] w-[8px] h-[8px]"
        style={{ backgroundColor: color }}
      />
      <div className="flex flex-col">
        <Typography variant="caption" className="text-gray-600">
          {label}
        </Typography>
        {subLabel && (
          <Typography variant="caption" className="text-gray-400">
            {subLabel}
          </Typography>
        )}
      </div>
    </div>
    <Typography variant="caption" className="text-gray-800 font-medium">
      {value}
    </Typography>
  </div>
);

/**
 * Tooltip shared by Charts 2 (Side-by-Side) and 3 (Stacked). Both visualise
 * the same per-day shape (`BowlingDay`) with the same metric rows.
 */
export const BowlingDayTooltip = ({
  row,
  phases,
  showAcwr,
  plannedColor,
  actualColor,
  acwrColor,
}: {
  row: BowlingDay;
  phases: PhaseSpan[];
  showAcwr: boolean;
  plannedColor: string;
  actualColor: string;
  acwrColor: string;
}) => {
  const { t } = useTranslation();
  const fmt = (v: number | null): string => (v !== null ? v.toString() : '—');
  const fmtAcwr = (v: number | null): string =>
    v !== null ? v.toFixed(2) : '—';

  return (
    <TooltipShell day={row.day} phases={phases}>
      <TooltipRow
        color={plannedColor}
        label={t('planned')}
        value={row.planned.toString()}
      />
      <TooltipRow
        color={actualColor}
        label={t('actuals')}
        value={fmt(row.actual)}
        last={!showAcwr}
      />
      {showAcwr && (
        <TooltipRow
          color={acwrColor}
          label="ACWR"
          value={fmtAcwr(row.acwr)}
          last
        />
      )}
    </TooltipShell>
  );
};
